import moment, { Moment } from 'moment-timezone';
import business from 'moment-business';
import { ColorPaletteProps } from '../types';
import { ILocation, ISchedule } from '../models/ILocation';
import { IWeekDaySchedule } from '../models/IService';
import { CustomShift, IEmployee } from '../models/IEmployee';
import { FormikValues } from 'formik';
import { CalendarModeValues, CancellationReason } from '../views/calendar/types';

export const APP_PHONE_FORMAT = '+# (###) ###-####';

export interface WorkingHours {
    start: string;
    startMoment?: Moment;
    end: string;
    endMoment?: Moment;
    businessHours?: {
        startTime: string;
        endTime: string;
    };
}

export const gridSpacing = 3;
export const drawerWidth = 260;
export const appDrawerWidth = 320;

// conversation status constants
export const statuses = {
    active: { status: 1, name: 'active' },
    closed: { status: 3, name: 'closed' },
    spam: { status: 4, name: 'spam' },
    snoozed: { status: 6, name: 'snoozed' }
};

export const inboxFolderTypes = {
    mine: 20,
    starred: 25,
    drafts: 30,
    assigned: 40,
    closed: 60,
    spam: 80,
    deleted: 110,
    snoozed: 200
};

// snooze conversation options
export const snoozePresets = [
    {
        id: 1,
        title: 'This Evening',
        dateTitle: '6:00 PM',
        date: moment().set({ hour: 18, minute: 0, seconds: 0 }).toJSON()
    },
    {
        id: 2,
        title: 'Later Today',
        dateTitle: '7:00 PM',
        date: moment().set({ hour: 19, minute: 0, seconds: 0 }).toJSON()
    },
    {
        id: 3,
        title: 'Tomorrow',
        dateTitle: business.addWeekDays(moment().set({ hour: 9, minute: 0, seconds: 0 }), 1).format('lll'),
        date: business.addWeekDays(moment().set({ hour: 9, minute: 0, seconds: 0 }), 1).toJSON()
    },
    {
        id: 4,
        title: 'This Week',
        dateTitle: business.addWeekDays(moment().set({ hour: 9, minute: 0, seconds: 0 }), 2).format('lll'),
        date: business.addWeekDays(moment().set({ hour: 9, minute: 0, seconds: 0 }), 2).toJSON()
    },
    {
        id: 5,
        title: 'Next Week',
        dateTitle: moment().day(1).add(7, 'days').set({ hour: 9, minute: 0, seconds: 0 }).format('lll'),
        date: moment().day(1).add(7, 'days').set({ hour: 9, minute: 0, seconds: 0 }).toJSON()
    }
];

export const stringToColor = (string: string) => {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.substr(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
};

// staff colors
interface Colors {
    lightBlue: ColorPaletteProps;
    purple: ColorPaletteProps;
    green: ColorPaletteProps;
    deepOrange: ColorPaletteProps;
    red: ColorPaletteProps;
    amber: ColorPaletteProps;
    blue: ColorPaletteProps;
    black: ColorPaletteProps;
    white: ColorPaletteProps;
}

export const colors: Colors = {
    lightBlue: {
        value: '#0D99FF',
        label: 'Light Blue'
    },
    purple: {
        value: '#673AB7',
        label: 'Purple'
    },
    green: {
        value: '#14AE5C',
        label: 'Green'
    },
    deepOrange: {
        value: '#FFA629',
        label: 'Deep Orange'
    },
    red: {
        value: '#F24822',
        label: 'Red'
    },
    amber: {
        value: '#FFC107',
        label: 'Amber'
    },
    blue: {
        value: '#250DFF',
        label: 'Blue'
    },
    black: {
        value: '#212121',
        label: 'Black'
    },
    white: {
        value: '#ffffff',
        label: 'White'
    }
};

export const defaultWorkingHours: WorkingHours = {
    start: '00:00:00',
    end: '23:59:59'
};

export const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const appDateFormat = 'MM/DD/YY';
export const appDateTimeFormat = 'MM/DD/YY HH:mm:ss';
export const apiTimeFormat = 'YYYY-MM-DD HH:mm:ss';
export const apiReportDateFormat = 'YYYY-MM-DD';

export const in_advance_default: number = 30;

export const initSchedule: ISchedule[] = weekDays.map((day, index) => ({
    id: index,
    label: day,
    enable: false,
    start: moment().set({ hours: 8, minutes: 30 }).format(apiTimeFormat),
    end: moment().set({ hours: 17, minutes: 30 }).format(apiTimeFormat)
}));

export const initServiceSchedule: IWeekDaySchedule[] = Array.from(Array(7).keys()).map((day) => ({
    id: day,
    enable: true
}));

export const getWidgetMaxBookingDate = (maxAdvanceBooking: number) =>
    maxAdvanceBooking > 0 ? moment().add(maxAdvanceBooking, 'days') : undefined;

export const getHumanizedDuration = (minutes: number) => {
    const hoursNum = Math.floor(minutes / 60);
    if (minutes === 60) {
        return '1h';
    }
    if (hoursNum) {
        return minutes % 60 > 0 ? `${hoursNum}h ${minutes % 60}m` : `${hoursNum}h`;
    }
    return `${minutes}m`;
};

export const removeZeroMinutes = (time: Moment) => (time.minutes() === 0 ? time.format('h A') : time.format('h:mm A'));

interface getMinMaxEventTimeProps {
    events: FormikValues[];
    mode: 'week' | 'day';
    date: string;
    time_zone: string;
}

const getEventStartMoment = (event: FormikValues, date: string) => {
    if (event.startTime) {
        const time = moment(event.startTime, 'HH:mm:ss');
        return moment(date).set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: time.get('second')
        });
    }

    return moment(event.start);
};

const getEventEndMoment = (event: FormikValues, date: string) => {
    if (event.endTime) {
        const time = moment(event.endTime, 'HH:mm:ss');
        return moment(date).set({
            hour: time.get('hour'),
            minute: time.get('minute'),
            second: time.get('second')
        });
    }

    return moment(event.end);
};

export const getMinMaxEventTime = ({ events, mode, date, time_zone }: getMinMaxEventTimeProps) => {
    if (mode === 'day') {
        const todayEvents = events.filter((event) => moment.tz(date, time_zone).isSame(event.start, 'day'));
        const dayEventsStartMoments = todayEvents.map((event) => getEventStartMoment(event, date));
        const dayEventsEndMoments = todayEvents.map((event) => getEventEndMoment(event, date));
        return {
            min: todayEvents.length > 0 ? moment.min(dayEventsStartMoments) : null,
            max: todayEvents.length > 0 ? moment.max(dayEventsEndMoments) : null
        };
    }
    const weekEventsStartMoments = events.map((event) => getEventStartMoment(event, date));
    const weekEventsEndMoments = events.map((event) => getEventEndMoment(event, date));

    return {
        min: weekEventsStartMoments.length ? moment.min(weekEventsStartMoments) : null,
        max: weekEventsEndMoments.length ? moment.max(weekEventsEndMoments) : null
    };
};

interface getDayWorkingHoursProps {
    location: ILocation;
    date: string;
    events: FormikValues[];
    view: string;
    employees: IEmployee[];
}

// Taking into account providers with opened custom shifts on current location
const filterAffectedProviders = (providers: IEmployee[], locationId: number) =>
    [...providers].filter(({ locations }) =>
        locations.some(({ id, pivot }) => {
            const hasOpenedCustomShifts = pivot?.is_shifts_enabled && pivot?.shifts?.some((shift) => shift.opened);
            const notUseLocationSchedule = pivot && !pivot.use_location_schedule;

            return id === locationId && (hasOpenedCustomShifts || notUseLocationSchedule);
        })
    );

// Calculate location custom shifts matching specific week
const customLocShiftForWeek = (location: ILocation, day: string) => {
    if (location.is_shifts_enabled && location.shifts?.length) {
        return location.shifts.filter((shift) => moment(shift.start, 'YYYY-MM-DD HH:mm').isSame(moment(day, 'YYYY-MM-DD HH:mm'), 'week'));
    }

    return [];
};

type LocBusinessHoursEntry = {
    daysOfWeek: [number];
    startTime: string;
    endTime: string;
};

const getLocationBusinessHours = (location: ILocation, day: string) => {
    if (location.schedule?.length) {
        const sameWeekShifts = customLocShiftForWeek(location, day);

        return location.schedule.reduce<LocBusinessHoursEntry[]>((acc, current) => {
            const id = parseInt(`${current.id}`, 10);
            const linkedCustomShift = sameWeekShifts.find((shift) => moment(shift.start).day() <= id && moment(shift.end).day() >= id);
            const enabled = linkedCustomShift ? linkedCustomShift.opened : current.enable;

            if (enabled) {
                return [
                    ...acc,
                    {
                        daysOfWeek: [id],
                        startTime: moment(linkedCustomShift?.start ?? current.start, 'YYYY-MM-DD HH:mm').format('HH:mm:ss'),
                        endTime: moment(linkedCustomShift?.end ?? current.end, 'YYYY-MM-DD HH:mm').format('HH:mm:ss')
                    }
                ];
            }

            return acc;
        }, []);
    }

    return false;
};

const normalizeTimes = (day: string, times: string[]) => times.filter((time) => time).map((time) => moment(`${day} ${time}`));

export const getCalendarWorkingHours = ({ location, date, events, view, employees }: getDayWorkingHoursProps) => {
    const providers = filterAffectedProviders(employees, location.id);

    // Day Mode
    if ((view === CalendarModeValues.Day || view === CalendarModeValues.ResourceDay) && location.schedule.length > 0) {
        // get current selected calendar day
        const day = moment.tz(date, location.time_zone).format('YYYY-MM-DD');
        const dayId = moment(day).day();

        // get location schedule for today
        const daySchedule = location.schedule.find((schedule) => Number(schedule.id) === moment(date).day())!;
        const scheduleStartTime = moment(daySchedule.start, 'YYYY-MM-DD HH:mm').format('HH:mm:ss');
        const scheduleEndTime = moment(daySchedule.end, 'YYYY-MM-DD HH:mm').format('HH:mm:ss');

        // get today custom shifts
        const todayCustomShift = getShiftByDate(day, location.shifts);
        const customStart = todayCustomShift ? moment(todayCustomShift.start, 'YYYY-MM-DD HH:mm').format('HH:mm:ss') : '';
        const customEnd = todayCustomShift ? moment(todayCustomShift.end, 'YYYY-MM-DD HH:mm').format('HH:mm:ss') : '';

        // calculates min-max times from incoming events for today
        const minMaxFromEvent = getMinMaxEventTime({
            events,
            time_zone: location.time_zone,
            date,
            mode: 'day'
        });

        // Adding providers business hours start end to expand calendar bounds accordingly
        const providerTimes = providers
            .map((provider) => getEmployeeBusinessTime({ dayId, date, location, employee: provider }))
            .filter((provider) => provider.startTime !== '00:00' && provider.endTime !== '00:00');
        const providerStartTimes = providerTimes.map(({ startTime }) => startTime);
        const providerEndTimes = providerTimes.map(({ endTime }) => endTime);

        const startTimes = normalizeTimes(day, [
            scheduleStartTime,
            customStart,
            minMaxFromEvent.min ? minMaxFromEvent.min.format('HH:mm') : '',
            ...providerStartTimes
        ]);

        const endTimes = normalizeTimes(day, [
            scheduleEndTime,
            customEnd,
            minMaxFromEvent.max ? minMaxFromEvent.max.format('HH:mm') : '',
            ...providerEndTimes
        ]);

        const startMoment = moment.min(startTimes).startOf('hour');
        const endMoment = moment.max(endTimes).endOf('hour');

        return {
            start: startMoment.format('HH:mm:ss'),
            end: startMoment.isSame(endMoment, 'day') ? endMoment.format('HH:mm:ss') : defaultWorkingHours.end,
            businessHours: getLocationBusinessHours(location, day)
        };
    }

    // Week Mode
    if (view === CalendarModeValues.Week && location.schedule.length > 0) {
        const weekStart = moment.tz(date, location.time_zone).startOf('week').format('YYYY-MM-DD');
        const scheduleStartTimes = location.schedule.map(({ start }) => moment(start).format('HH:mm:ss'));
        const scheduleEndTimes = location.schedule.map(({ end }) => moment(end).format('HH:mm:ss'));

        const weekCustomShifts = customLocShiftForWeek(location, date);

        const customStartTimes = weekCustomShifts.map(({ start }) => moment(start).format('HH:mm:ss'));
        const customEndTimes = weekCustomShifts.map(({ end }) => moment(end).format('HH:mm:ss'));

        const minMaxFromEvent = getMinMaxEventTime({
            events,
            time_zone: location.time_zone,
            date,
            mode: 'week'
        });

        const startTimes = normalizeTimes(weekStart, [
            ...scheduleStartTimes,
            ...customStartTimes,
            minMaxFromEvent.min ? minMaxFromEvent.min.format('HH:mm') : ''
        ]);

        const endTimes = normalizeTimes(weekStart, [
            ...scheduleEndTimes,
            ...customEndTimes,
            minMaxFromEvent.max ? minMaxFromEvent.max.format('HH:mm') : ''
        ]);

        const startMoment = moment.min(startTimes).startOf('hour');
        const endMoment = moment.max(endTimes).endOf('hour');

        return {
            start: startMoment.format('HH:mm:ss'),
            end: startMoment.isSame(endMoment, 'day') ? endMoment.format('HH:mm:ss') : defaultWorkingHours.end,
            businessHours: getLocationBusinessHours(location, date)
        };
    }

    // Month Mode
    if (view === CalendarModeValues.Month && location.schedule.length > 0) {
        const startTimesArray = location.schedule.map((schedule) => moment(schedule.start));
        const endTimesArray = location.schedule.map((schedule) => moment(schedule.end));
        return {
            start: startTimesArray.length > 0 ? moment.min(startTimesArray).startOf('hour').format('HH:mm:ss') : defaultWorkingHours.start,
            end: endTimesArray.length > 0 ? moment.max(endTimesArray).endOf('hour').format('HH:mm:ss') : defaultWorkingHours.end
        };
    }
    return defaultWorkingHours;
};

interface CancellationValue {
    title: string;
    value: CancellationReason;
}

export const cancellationReasons: Record<CancellationReason, CancellationValue> = {
    customer_canceled: {
        title: 'Customer Canceled',
        value: CancellationReason.Customer_canceled
    },
    customer_no_show: {
        title: 'Customer No Show',
        value: CancellationReason.Customer_no_show
    },
    staff_canceled: {
        title: 'Staff Canceled',
        value: CancellationReason.Staff_canceled
    },
    deposit_expiration: {
        title: 'Deposit Expiration',
        value: CancellationReason.Deposit_expiration
    }
};

export interface CellData {
    value: number;
    duration: string;
    interval: string;
}

export interface CalendarCellInterval {
    [key: number]: CellData;
}

export const calendarCellDurations: CalendarCellInterval = {
    30: {
        value: 30,
        duration: '00:30:00',
        interval: '00:30'
    },
    60: {
        value: 60,
        duration: '01:00:00',
        interval: '01:00'
    }
};

export const min_service_reschedule_interval = 0;

type GetEmployeeBusinessTime = {
    date: string;
    employee: IEmployee;
    dayId: number;
    location: ILocation;
};

export const getEmployeeBusinessTime = ({ dayId, employee, date, location }: GetEmployeeBusinessTime) => {
    const nonWorkingDay = {
        startTime: '00:00',
        endTime: '00:00'
    };

    const usedLocation = employee.locations.find((loc) => loc.id === location.id);
    const schedule = !!usedLocation && !usedLocation.pivot.use_location_schedule ? usedLocation.pivot.schedule : location.schedule;
    const { is_shifts_enabled, shifts } = usedLocation?.pivot || {
        is_shifts_enabled: false,
        shifts: [],
        schedule: []
    };

    if (is_shifts_enabled) {
        const customShift = getShiftByDate(date, shifts);

        if (customShift)
            return customShift.opened
                ? {
                      startTime: moment(customShift.start, apiTimeFormat).format('HH:mm'),
                      endTime: moment(customShift.end, apiTimeFormat).format('HH:mm'),
                      daysOfWeek: [dayId]
                  }
                : nonWorkingDay;
    }

    const matchingLocShift = getShiftByDate(date, location.shifts);
    const targetDaySchedule = schedule.find((day) => Number(day.id) === dayId);
    const enabled = matchingLocShift && location.is_shifts_enabled ? matchingLocShift.opened : !!targetDaySchedule?.enable;

    return enabled && targetDaySchedule
        ? {
              startTime: moment(matchingLocShift?.start ?? targetDaySchedule.start, apiTimeFormat).format('HH:mm'),
              endTime: moment(matchingLocShift?.end ?? targetDaySchedule.end, apiTimeFormat).format('HH:mm'),
              daysOfWeek: [dayId]
          }
        : nonWorkingDay;
};

export const getShiftByDate = (date: Date | Moment | string, shifts: CustomShift[]) => {
    const startTime = { hour: 0, minute: 0, second: 0 };

    return shifts.find((shift) =>
        moment(date).set(startTime).isBetween(moment(shift.start).set(startTime), moment(shift.end).set(startTime), null, '[]')
    );
};
